/**
 * PercentVariantSelect — a compact Select for the TWO percent variants (`percentage`
 * fraction ⇔ `percent_points`), used where a field is already known to be a
 * percentConfigurable percent field and only the variant is chosen: the Fields page
 * Standard editor and the mapping row for a percentConfigurable standard target.
 *
 * Unlike {@link TypeSubtypeSelect} it offers no type axis and no other subtypes — the
 * type is fixed (numeric percent), only the variant is the customer's choice. With a
 * `numericMax` hint the variant `defaultPercentVariant` implies (a stored max > 1 reads
 * as over 100% under the fraction variant) is marked "matches your data" — a visible
 * recommendation, never an override. Pure: owns no state, calls `onChange(variant)`.
 */
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { defaultPercentVariant, type PercentSubtype } from '@repo/shared/common';
import { Select } from '~/components/ui';

const PERCENT_VARIANTS: readonly PercentSubtype[] = ['percentage', 'percent_points'];

export interface PercentVariantSelectProps {
  /** The current percent variant. */
  readonly value: PercentSubtype;
  /** Called with the next variant when it changes. */
  readonly onChange: (variant: PercentSubtype) => void;
  readonly disabled?: boolean;
  /** Mantine input size (default `sm`). */
  readonly size?: string;
  /** Render a visible "Format" label above the select. Off by default. */
  readonly withLabel?: boolean;
  /** id/aria base for the select, e.g. `idBase="interest-rate"`. */
  readonly idBase?: string;
  /**
   * Smart-percent hint: the field's maximum stored numeric value, or null when
   * unknown. When set, the variant `defaultPercentVariant(numericMax)` implies is
   * marked "matches your data" — a recommendation only; every explicit pick is honored.
   */
  readonly numericMax?: number | null;
}

export function PercentVariantSelect({
  value,
  onChange,
  disabled = false,
  size = 'sm',
  withLabel = false,
  idBase,
  numericMax = null,
}: PercentVariantSelectProps): ReactNode {
  const { t } = useTranslation();

  const impliedPercent = numericMax !== null ? defaultPercentVariant(numericMax) : null;
  const options = PERCENT_VARIANTS.map((variant) => ({
    value: variant,
    label:
      variant === impliedPercent
        ? t('numericSubtypes.matchesData', { label: t(`numericSubtypes.${variant}`) })
        : t(`numericSubtypes.${variant}`),
  }));

  return (
    <Select
      aria-label={t('numericSubtypes.subtypeLabel')}
      {...(withLabel ? { label: t('numericSubtypes.subtypeLabel') } : {})}
      data={options}
      value={value}
      onChange={(next) => {
        // Mantine narrows `next` to the typed-`data` value union (PercentSubtype).
        if (next === null || next === value) return;
        onChange(next);
      }}
      allowDeselect={false}
      disabled={disabled}
      size={size}
      comboboxProps={{ withinPortal: true }}
      {...(idBase !== undefined ? { id: `${idBase}-variant` } : {})}
    />
  );
}
